import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import DataManager from '@americanexpress/data-manager';
import SharedScript from 'axp-shared-scripts';
import { AmexDDLRegister, getAmexDataManagerConfig } from 'axp-data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import { ONE_CMS_CSS, ErrorBoundary, V1_V2X_TRANSACTION_COMPLETION } from '@americanexpress/gravity-core';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import childRoutes from '../childRoutes';
import reducer, { QUERYPARAMS, SETAPIURL } from '../ducks';
import Waitscreen from './WaitScreen';
import './waitscreen.scss';
import GravityRequestHandler from './GravityRequestHandler';
import WebAuthnEligibility from './WebAuthnEligibility';
import ScamPage from './scam/ScamPage';
/* istanbul ignore next */
export const GravityRoot = (props) => {
  const {
    gemUrl,
    updateQueryParams,
    location: { search, pathname },
    children,
    spinner,
    spinnerLogo,
    gravityCheckoutUrl,
    gravityAuthnUrl,
    mockProcessPayAuthApi,
    setApiUrl,
    queryParams,
    reportError,
    screenId,
    incomingReq,
    completion,
    router,
    logger,
    inauthConfig,
    updateDataHeader,
    dataHeader,
    updateSpinner,
    updateRootDialog,
    updateRootAbandonData,
    nodeEnv,
  } = props;

  const [completionData, setCompletionData] = useState(null);
  const [scamScreen, setScamScreen] = useState('');
  const [intervalStarted, setIntervalStarted] = useState(false);

  /* istanbul ignore next */
  // eslint-disable-next-line max-params
  const callHealthCheck = (apiUrl, options, acsTransId, firstCall = false) => {
    logger({
      apiUrl,
      stage: `${firstCall ? 'FIRST_' : ''}HEALTH_CHECK_REQUEST`,
      acsTransId,
    });
    let httpStatus;
    fetch(apiUrl, options)
      .then((response) => {
        const log = {
          apiUrl,
          status: response.status,
          stage: 'HEALTH_CHECK_RESPONSE',
          acsTransId,
        };
        logger(log, { error: null });

        if (!response.ok) {
          httpStatus = response.status;
          throw new Error(response.statusText);
        }
      })
      .catch((error) => {
        setTimeout(() => {
          const log = {
            apiUrl,
            status: httpStatus,
            stage: 'HEALTH_CHECK_ERROR_RESPONSE',
            acsTransId,
          };
          logger(log, { error });
        }, 300);
      });
  };

  /* istanbul ignore next */
  useEffect(() => {
    Intl.__disableRegExpRestore();

    const apiUrl = `${gravityCheckoutUrl}/health`;
    const headers = {
      Accept: 'application/json',
      'acs-trans-id': (incomingReq || {}).acsTransId,
    };
    const options = {
      method: 'GET',
      headers,
    };

    callHealthCheck(apiUrl, options, (incomingReq || {}).acsTransId, true);
  }, []);

  /* istanbul ignore next */
  const handleCompleteTranscation = (scamScreenId) => {
    updateRootDialog(false);
    const completionPayload = {
      screenId: scamScreenId,
      eventCode: 'screenTimeout',
    };
    setCompletionData(completionPayload);
  };

  /* istanbul ignore next */
  useEffect(() => {
    if (!intervalStarted && queryParams.acsTransId && (queryParams.healthCheckData || {}).enabled) {
      const apiUrl = `${gravityCheckoutUrl}/health`;
      const headers = {
        Accept: 'application/json',
        'acs-trans-id': queryParams.acsTransId,
      };
      const options = {
        method: 'GET',
        headers,
      };

      setInterval(() => {
        callHealthCheck(apiUrl, options, queryParams.acsTransId);
      }, queryParams.healthCheckData.interval);
      setIntervalStarted(true);
    }
  }, [queryParams.acsTransId]);

  /* istanbul ignore next */
  useEffect(() => {
    if (queryParams.scamContentCheck) {
      updateRootDialog(true);
      updateSpinner(false);
      const apiUrl = `${queryParams.mockEnabled === 'Y' ? mockProcessPayAuthApi : gravityCheckoutUrl}`;
      const postUrl = `${apiUrl}${V1_V2X_TRANSACTION_COMPLETION}${
        queryParams.mockEnabled === 'Y' ? '/mock' : ''
      }`;

      updateRootAbandonData({
        postUrl, queryParams, incomingReq, scamScreen,
      });
    }
  }, [queryParams.scamContentCheck, scamScreen, queryParams.acsTransId]);

  useEffect(() => {
    if (!queryParams.acsTransId || router.location.search) {
      const params = new URLSearchParams(router.location.search);
      const mockEnabled = params.get('mockEnabled');
      const queryparams = {
        ...queryParams,
      };

      if (mockEnabled && params.get('isScam')) queryparams.scamContentCheck = true;

      if (mockEnabled) {
        queryparams.acsTransId = params.get('acs-trans-id');
        queryparams.mockEnabled = mockEnabled;
        queryparams.token = params.get('token');
        queryparams.locale = params.get('locale');
        queryparams.props = JSON.parse(params.get('props'));
      }

      updateQueryParams(queryparams);
      setApiUrl(SETAPIURL, {
        queryparams,
        gravityCheckoutUrl,
        mockProcessPayAuthApi,
      });
    }
  }, [router.location.search, queryParams.acsTransId]);

  useEffect(() => {
    const headStyleSafeKey = document.createElement('link');
    headStyleSafeKey.rel = 'stylesheet';
    headStyleSafeKey.href = `${gemUrl}${ONE_CMS_CSS}/safeKey.css`;
    document.head.appendChild(headStyleSafeKey);

    const headStyleDLS = document.createElement('link');
    headStyleDLS.rel = 'stylesheet';
    headStyleDLS.href = 'https://www.aexp-static.com/cdaas/one/statics/axp-dls/6.8.0/package/dist/6.8.0/styles/dls.min.css';
    document.head.appendChild(headStyleDLS);
  }, []);

  useEffect(() => {
    if (incomingReq && incomingReq.type === 'PaReq') {
      if (incomingReq.acsTransId === 'Unable to decode acsTransId') {
        logger({
          name: 'INAUTH_SCRIPT_LOAD_FAILURE',
          acsTransId: incomingReq.acsTransId,
          reason: 'Unable to decode acsTransId',
        });
      } else if (incomingReq.acsTransId) {
        const uuid = incomingReq.acsTransId;
        window.inauth = window.inauth || {};
        /* eslint no-underscore-dangle: 0 */
        /* eslint no-undef: 0 */
        inauth._cc = inauth._cc || [];
        inauth._cc.push(['ci', { sid: 'ee490b8fb9a4d570', tid: uuid, clientName: 'Safekey' }]);
        inauth._cc.push(['st', 500]);
        inauth._cc.push(['cf', 134683635]);
        inauth._cc.push(['run', inauthConfig]);

        const headScript = document.createElement('script');
        headScript.id = 'inauth';
        headScript.type = 'text/javascript';
        headScript.async = true;
        headScript.src = `${inauthConfig}/cc.js?&sid=ee490b8fb9a4d570&tid=${uuid}&ts=${new Date().getTime()}&namespace=inauth`;
        document.head.appendChild(headScript);
        logger({
          name: 'INAUTH_SCRIPT_LOAD_SUCCESS',
          acsTransId: uuid,
        });
      }
    }
  }, []);

  /* istanbul ignore next */
  const handleContinue = () => {
    updateQueryParams({ ...queryParams, scamContentCheck: false });
    updateSpinner(true);
    updateRootDialog(false);
    setTimeout(() => {
      router.replace('/');
    }, 0);
  };

  return (
    <Fragment>
      <Helmet
        {...{
          title: 'SafeKey',
          link: [
            { rel: 'icon', href: 'https://www.americanexpress.com/favicon.ico' },
          ],
          meta: [
            { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
            { charset: 'utf-8' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
            { name: 'theme-color', content: '#006FCF' },
          ],
          htmlAttributes: { lang: `${queryParams.locale || 'en'}` },
        }}
      />
      <DataManager {...getAmexDataManagerConfig()}>
        <AmexDDLRegister />
        <DDLRegister name="user.authenticated" value={false} />
        <DDLRegister name="device.channel" value="Desktop" />
        {nodeEnv === 'prod' && (
        <SharedScript
          name="one-stream-data-handler"
          windowCheck="oneStreamData"
          version="^0.1.0"
        />
        )}
        <WebAuthnEligibility
          acsTransId={queryParams.acsTransId}
          token={queryParams.token}
          gravityAuthnUrl={gravityAuthnUrl}
          logger={logger}
          updateDataHeader={updateDataHeader}
          dataHeader={dataHeader}
        />
        <GravityRequestHandler
          incomingReq={incomingReq}
          completion={completion}
          pathname={pathname}
          gravityCheckoutUrl={gravityCheckoutUrl}
          gravityAuthnUrl={gravityAuthnUrl}
          queryParams={queryParams}
          router={router}
          logger={logger}
          updateDataHeader={updateDataHeader}
          dataHeader={dataHeader}
          completionData={completionData}
          updateQueryParams={updateQueryParams}
        >
          <div id="safekeyMain" className="safekey-main dls-white-bg">
            <ErrorBoundary
              moduleID="axp-gravity-root"
              acsTransId={queryParams.acsTransId || new URLSearchParams(search).get('acs-trans-id')}
              reportError={reportError}
              showHeader={true}
            >
              <Waitscreen
                show={spinner}
                gemUrl={gemUrl}
                showLogo={spinnerLogo}
                screenId={screenId}
                gravityAuthnUrl={gravityAuthnUrl}
                acsTransId={queryParams.acsTransId}
                token={queryParams.token}
                locale={queryParams.locale}
                updateDataHeader={updateDataHeader}
                dataHeader={dataHeader}
              />
              {
              /* istanbul ignore next */
              queryParams.scamContentCheck && queryParams.acsTransId
                ? (
                  <ScamPage
                    handleContinue={handleContinue}
                    handleCompleteTranscation={handleCompleteTranscation}
                    queryParams={queryParams}
                    setScamScreen={setScamScreen}
                    gravityAuthnUrl={gravityAuthnUrl}
                  />
                )
                : children
              }
            </ErrorBoundary>
          </div>
        </GravityRequestHandler>
      </DataManager>
    </Fragment>
  );
};

GravityRoot.childRoutes = childRoutes;

GravityRoot.holocron = {
  name: 'axp-gravity-root',
  reducer,
};

GravityRoot.propTypes = {
  children: PropTypes.node.isRequired,
  gemUrl: PropTypes.string.isRequired,
  updateQueryParams: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  spinner: PropTypes.bool.isRequired,
  spinnerLogo: PropTypes.bool.isRequired,
  gravityCheckoutUrl: PropTypes.string.isRequired,
  gravityAuthnUrl: PropTypes.string.isRequired,
  mockProcessPayAuthApi: PropTypes.string.isRequired,
  setApiUrl: PropTypes.func.isRequired,
  queryParams: PropTypes.shape({
    acsTransId: PropTypes.string,
    mockEnabled: PropTypes.string,
    token: PropTypes.string,
    locale: PropTypes.string,
    scamContentCheck: PropTypes.bool,
    inAuth: PropTypes.string,
    healthCheckData: PropTypes.shape({
      interval: PropTypes.number,
      enabled: PropTypes.bool,
    }),
  }).isRequired,
  reportError: PropTypes.func.isRequired,
  screenId: PropTypes.string.isRequired,
  incomingReq: PropTypes.shape({
    acsTransId: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
    }),
  }).isRequired,
  completion: PropTypes.shape({}).isRequired,
  logger: PropTypes.func.isRequired,
  inauthConfig: PropTypes.string.isRequired,
  updateDataHeader: PropTypes.func.isRequired,
  updateRootDialog: PropTypes.func.isRequired,
  updateRootAbandonData: PropTypes.func.isRequired,
  updateSpinner: PropTypes.func.isRequired,
  dataHeader: PropTypes.string.isRequired,
};

/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require
  GravityRoot.appConfig = require('../config/appConfig').default;
}
/* istanbul ignore next */
export const mapDispatchToProps = (dispatch) => ({
  updateSpinner: (payload) => dispatch({ type: 'modules/axp-gravity-root/SETSPINNER', payload }),
  updateQueryParams: (payload) => dispatch({ type: QUERYPARAMS, payload }),
  setApiUrl: (type, payload) => dispatch({ type, payload }),
  updateDataHeader: (payload) => dispatch({ type: 'modules/axp-gravity-root/UPDATE_DATA_HEADER', payload }),
  updateRootDialog: (payload) => dispatch({ type: 'modules/axp-gravity-root/UPDATE_ROOT_SHOW_DIALOG', payload }),
  updateRootAbandonData: (payload) => dispatch({ type: 'modules/axp-gravity-root/UPDATE_ROOT_ABANDON_DATA', payload }),
  reportError: (
    error, moduleID, acsTransId
  ) => dispatch(addErrorToReport(
    error,
    {
      collectionMethod: 'componentDidCatch',
      moduleID,
      acsTransId,
    }
  )),
  logger: (
    log, error, collectionMethod
  ) => dispatch(addErrorToReport(
    error, { collectionMethod, ...log }
  )),
});

export const mapStateToProps = (state) => ({
  gemUrl: state.getIn(['config', 'gemUrl']),
  nodeEnv: state.getIn(['config', 'nodeEnv']),
  gravityCheckoutUrl: state.getIn(['config', 'gravityCheckoutUrl']),
  gravityAuthnUrl: state.getIn(['config', 'gravityAuthnUrl']),
  queryParams: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'acsTransId']),
  incomingReq: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'incomingReq']),
  completion: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'completion']),
  spinner: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'spinner']),
  spinnerLogo: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'spinnerLogo']),
  mockProcessPayAuthApi: state.getIn(['config', 'mockProcessPayAuthApiUrl']),
  screenId: state.getIn(['modules', 'axp-gravity-page-wrapper', 'currentScreenId']),
  inauthConfig: state.getIn(['config', 'inAuth']),
  dataHeader: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'dataHeader']),
});

const hocChain = compose(connect(mapStateToProps, mapDispatchToProps));

export default hocChain(GravityRoot);
